import { LoadingSpinner } from './LoadingSpinner';

export interface AllCampaignsAccessPreviewProps {
  campaigns: { id: string; name: string; brandName?: string }[];
  isLoading?: boolean;
}

/**
 * Read-only preview of "every campaign" access — shown in place of
 * CampaignAccessPicker wherever a role grants full access (Admin/admin),
 * so the actor sees exactly what that means before confirming instead of
 * just a one-line assurance. Same fixed-height box as
 * CampaignAccessPicker's own selected list for visual consistency;
 * nothing here is removable since it isn't a real per-campaign grant —
 * admin access is all-or-nothing.
 */
export function AllCampaignsAccessPreview({ campaigns, isLoading = false }: AllCampaignsAccessPreviewProps) {
  return (
    // h-80, not h-64 like CampaignAccessPicker's own box — this replaces
    // that box AND the search row above it (there's nothing to search for;
    // it's everything), so it's taller by roughly that row's height. Keeps
    // the two states the same total height instead of leaving a gap, and
    // Admin usually has more campaigns to show anyway.
    <div className="h-80 min-w-0 overflow-y-auto rounded-lg border border-border">
      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <LoadingSpinner size={24} className="text-muted-foreground" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="flex h-full items-center justify-center px-6 text-center">
          <p className="text-body-sm-regular text-muted-foreground">No campaigns yet.</p>
        </div>
      ) : (
        <ul>
          {campaigns.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 border-b border-border px-4 py-2">
              <div className="min-w-0">
                <div className="truncate text-body-sm-semibold text-foreground">{c.name}</div>
                {c.brandName && <div className="truncate text-caption-regular text-muted-foreground">{c.brandName}</div>}
              </div>
              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-caption-medium text-muted-foreground">Admin</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
